/**
 * The react-query keys of the native show surface: one vocabulary for the caches the
 * ported Tixologi pages share, so a mutation on one page invalidates what another reads.
 *
 * The strings are tixologist's (snapshot 2f0c9253), so a ported invalidation still matches
 * its query. Two rules are downstroke's own:
 * - **Event ids are strings**, the route param's shape (tracker Decision 3): `"42"` and
 *   `42` are different keys, so nothing here takes a number.
 * - **Partner caches carry the scope** (`[name, scopeId, ...]`). The iframe served one
 *   partner per page load; one downstroke QueryClient outlives a move between venues.
 *   With the scope at index 1, a ported literal such as `["orders", "comped"]` no longer
 *   prefixes its query: build the query and its invalidation from these factories.
 *
 * Each family caches the raw response of ONE endpoint and readers shape it in `select`,
 * so two readers never disagree on what the cache holds. A page's own discriminators
 * (search, sort, page) follow the prefix: `[...showKeys.responses(eventId), search]`.
 *
 * Every first element tixologist's green pages use (src/features/PunchupPages,
 * src/features/common, src/features/Dashboard/components/punchup, src/hooks):
 * - show, by event id: event, ticketTypes, eventPools, pool, canGoLive, custom-fields,
 *   event-highlights (its QUERY_KEYS_NO_FETCHING.EVENT_HIGHLIGHTS), responses,
 *   scan-history, event-scans, comped-inventory, applicableAddons, box-office-addons,
 *   lotteryEmailPreview, scanner-attendees, scanner-gates, gates,
 *   seats-chart-change-preview;
 * - show, by the entity's own id: ticket (a ticket type), ticket-details, ticket-history,
 *   seatsIOEvent, seatsIOEventReport;
 * - partner, by scope: partner-data, venue-box-office-fee (here `boxOfficeFee`, the name
 *   the venue settings hooks cache the same fee under), box-office-promotions, tags,
 *   scanner-partner-members, partnerSettings, orders, seats-charts; by the entity's own
 *   id: seats-chart; by the attendee's user id: fan-notes (the reader appends the scope,
 *   since a partner's notes on a user are not another partner's);
 * - left to the one page that reads them: box-office-totals (a cart quote),
 *   date-change-email-template (one template for every partner), and Seats Sync's
 *   seats-sync-status, seats-sync-preview, orphaned-seats (admin show/seats-sync/queries.ts);
 * - read by no ported page: highlights (partner-wide), partner-fans, promoCodeEvent,
 *   promocode, promocodes, events, users-me; and two event reads that now share `event` (GET
 *   /events/:id) with a `select` of their own: event-ticket-types (the pool forms) and
 *   sendTicketsEvent (Send Tickets).
 *
 * Not part of this vocabulary: downstroke's own Tixologi hooks. Their pools, highlights,
 * custom-field and tag hooks went with the dead native show sections (TIC-821, T0); the
 * live ones (event create, duplicate and delete, ticket-type bulk update) invalidate
 * `["showsWithTixologi"]`, a cache of Punchup shows that ported pages leave alone.
 */
import type { QueryClient, QueryKey } from "@tanstack/react-query";

/** The venue box office fee's cache name, shared with the settings hooks in `hooks/box-office-fee` (they import it from here, so this module stays a leaf). */
export const BOX_OFFICE_FEE_QUERY_KEY = "boxOfficeFee";

/** The families keyed `[name, eventId, ...]`: the ones `invalidateShowCaches` reaches. */
const SHOW_FAMILIES = new Set<string>();

/** A family keyed `[name, eventId]`, registered in SHOW_FAMILIES as it is defined. */
function showFamily<Name extends string>(name: Name) {
  SHOW_FAMILIES.add(name);
  return (eventId: string) => [name, eventId] as const;
}

/** A family keyed `[name, scopeId]`. */
const partnerFamily =
  <Name extends string>(name: Name) =>
  (scopeId: string) =>
    [name, scopeId] as const;

const poolFamily = showFamily("pool");
const seatsChartChangePreviewFamily = showFamily("seats-chart-change-preview");

/** Caches about one show. */
export const showKeys = {
  /** GET /events/:id, the event with its days and ticket types. */
  event: showFamily("event"),
  /** GET /public/ticketing/ticket_types?event_id=, the public ticket types. */
  ticketTypes: showFamily("ticketTypes"),
  /** GET /events/:id/pools. */
  eventPools: showFamily("eventPools"),
  pool: (eventId: string, poolId: string) =>
    [...poolFamily(eventId), poolId] as const,
  canGoLive: showFamily("canGoLive"),
  customFields: showFamily("custom-fields"),
  highlights: showFamily("event-highlights"),
  /** Custom-field responses; the page appends its search and sort. */
  responses: showFamily("responses"),
  scanHistory: showFamily("scan-history"),
  eventScans: showFamily("event-scans"),
  compedInventory: showFamily("comped-inventory"),
  /** The page appends the ticket-type ids it asked about. */
  applicableAddons: showFamily("applicableAddons"),
  /** The page appends the cart's tickets. */
  boxOfficeAddons: showFamily("box-office-addons"),
  lotteryEmailPreview: showFamily("lotteryEmailPreview"),
  scannerAttendees: showFamily("scanner-attendees"),
  scannerGates: showFamily("scanner-gates"),
  gates: showFamily("gates"),
  seatsChartChangePreview: (eventId: string, newChartKey: string) =>
    [...seatsChartChangePreviewFamily(eventId), newChartKey] as const,

  // Keyed by the entity's own id (unique across partners); invalidateShowCaches skips them.
  ticketType: (ticketTypeId: string) => ["ticket", ticketTypeId] as const,
  ticketDetails: (ticketId: string) => ["ticket-details", ticketId] as const,
  ticketHistory: (ticketId: string) => ["ticket-history", ticketId] as const,
  seatsEvent: (seatsEventKey: string) =>
    ["seatsIOEvent", seatsEventKey] as const,
  seatsEventReport: (seatsEventKey: string) =>
    ["seatsIOEventReport", seatsEventKey] as const,
};

/** Caches about the partner (venue or comedian) the scope logs in as. */
export const partnerKeys = {
  /** GET /partners/data. */
  data: partnerFamily("partner-data"),
  /**
   * GET /partners/box-office-fee, which ticketing-api serves (src/api-base.ts). The family
   * name is the venue settings hooks' own (`BOX_OFFICE_FEE_QUERY_KEY`, hooks/box-office-fee),
   * so the settings and the Box Office page read and invalidate one entry per venue.
   */
  boxOfficeFee: partnerFamily(BOX_OFFICE_FEE_QUERY_KEY),
  /** GET /promotions?status=active…, the Box Office promo-code picker. */
  boxOfficePromotions: partnerFamily("box-office-promotions"),
  /** GET /tags. A prefix of apps/web useTags's key, so invalidating it refreshes that too. */
  tags: partnerFamily("tags"),
  /** The partner's members, as Scan Tickets finds the scanner's own gate. */
  scannerMembers: partnerFamily("scanner-partner-members"),
  /** The partner's members, as Gates & Scanners manages them. */
  members: partnerFamily("partnerSettings"),
  /** GET /orders; Transactions, Comped Tickets and Send Tickets append their filters. */
  orders: partnerFamily("orders"),
  /** GET /seats/charts. apps/web's useGetSeatsCharts reads this same key: same body, one entry. */
  seatsCharts: partnerFamily("seats-charts"),

  // Keyed by the entity's own id (unique across partners).
  seatsChart: (chartKey: string) => ["seats-chart", chartKey] as const,
  /**
   * An attendee's fan notes, by the attendee's user id. The partner's notes on a user are not
   * another partner's, so a reader appends the scope (`[...fanNotes(userId), scopeId]`) and a
   * mutation invalidates this prefix to reach every partner's copy.
   */
  fanNotes: (userId: string) => ["fan-notes", userId] as const,
};

/** The prefix `[name]` of the family a key belongs to: it reaches every entry of that family, whatever its event or ticket id. */
const familyPrefix = (key: readonly [string, ...unknown[]]): QueryKey => [
  key[0],
];

/** `["responses"]`: the attendee list (and View Responses) of every event; the pages append their search and sort. */
export const attendeeListKey: QueryKey = familyPrefix(showKeys.responses(""));

/**
 * What a change to a ticket's state (transfer, cancel, un-cancel, refund, validate, switch) makes
 * stale: the attendee list, and every ticket's history and details. Family prefixes, since a
 * mutation hook does not know the ticket yet; invalidate them one `queryKey` at a time.
 */
export const ticketStateKeys: QueryKey[] = [
  attendeeListKey,
  familyPrefix(showKeys.ticketHistory("")),
  familyPrefix(showKeys.ticketDetails("")),
];

/** Refetches what a change to a ticket's state made stale (`ticketStateKeys`). */
export function invalidateTicketState(queryClient: QueryClient): void {
  ticketStateKeys.forEach((queryKey) =>
    queryClient.invalidateQueries({ queryKey })
  );
}

/**
 * How long a partner's seating-chart list stays fresh, for every reader of
 * `partnerKeys.seatsCharts`: the native show's picker and apps/web's create-show wizard
 * share the entry, so they share the policy that refetches it.
 */
export const SEATS_CHARTS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Marks every by-event-id cache of the show stale, refetching the ones on screen: for a
 * change that touches the whole show. Entity-keyed and partner caches are not reached;
 * invalidate those by their own keys.
 */
export function invalidateShowCaches(
  queryClient: QueryClient,
  eventId: string
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: ({ queryKey: [family, id] }) =>
      typeof family === "string" && SHOW_FAMILIES.has(family) && id === eventId,
  });
}
